// src/components/ui/assets/AssetPreview.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Tooltip,
  Link,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  VideoCameraBack as VideoIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  AudioFile as AudioIcon,
} from '@mui/icons-material';
import { getSignedFileUrl } from '../../services/api';
import { selectBestVersion, isImageFile } from '../../utils/fileHelpers';
import ImageViewer from './ImageViewer';

/**
 * Component for displaying an asset preview with actions and secure URLs
 */
const AssetPreview = ({ 
  asset, 
  onDelete, 
  onView,
  showActions = true,
  sx = {},
  ...props 
}) => {
  const theme = useTheme();
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Determine if the asset URL is local or needs a signed URL
  const isLocal = asset.url?.includes('blob') || asset.file;
  const assetKey = asset.url && !isLocal ? (asset.key || asset.url) : null;
  
  // Get signed URL for non-local assets
  useEffect(() => {
    if (assetKey) {
      setIsLoading(true);
      getSignedFileUrl(assetKey)
        .then(url => {
          setSignedUrl(url);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching signed URL:', error);
          setIsLoading(false);
        });
    }
  }, [assetKey]);

  // Get the effective URL to use
  const getEffectiveUrl = () => {
    if (isLocal) {
      return asset.url || (asset.file && URL.createObjectURL(asset.file));
    }
    return signedUrl;
  };

  // Determine file type
  const getAssetType = () => {
    if (asset.type) return asset.type;
    
    if (asset.mimetype) {
      if (asset.mimetype.startsWith('image/')) return 'image';
      if (asset.mimetype.startsWith('video/')) return 'video';
      if (asset.mimetype.startsWith('audio/')) return 'audio';
      if (asset.mimetype === 'application/pdf') return 'pdf';
      return 'document';
    }
    
    // Try to guess from file extension
    const fileName = asset.originalname || asset.key || '';
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
    if (ext === 'pdf') return 'pdf';
    
    return 'document';
  };
  
  const assetType = getAssetType();
  
  // Get appropriate icon
  const getAssetIcon = () => {
    switch (assetType) {
      case 'image': return <ImageIcon fontSize="large" />;
      case 'video': return <VideoIcon fontSize="large" />;
      case 'audio': return <AudioIcon fontSize="large" />;
      case 'pdf': return <PdfIcon fontSize="large" />;
      default: return <DocumentIcon fontSize="large" />;
    }
  };
  
  // Render preview based on asset type
  const renderPreview = () => {
    const effectiveUrl = getEffectiveUrl();
    
    // Show loading indicator while fetching signed URL
    if (isLoading) {
      return (
        <Box 
          sx={{ 
            height: 160, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    switch (assetType) {
      case 'image':
        return (
          <Box sx={{ 
            height: 160, 
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}>
            <ImageViewer
              file={asset}
              displayType="preview"
              alt={asset.originalname || asset.key || 'Image'}
              sx={{ height: 160, width: '100%' }}
              style={{ objectFit: 'cover' }}
              fallbackToOriginal={true}
            />
          </Box>
        );
      case 'video':
        return (
          <Box sx={{ position: 'relative', height: 160 }}>
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#000', 0.7),
              }}
            >
              <VideoIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            {effectiveUrl && (
              <CardMedia
                component="video"
                height={160}
                src={effectiveUrl}
                controls={false}
                sx={{ 
                  objectFit: 'cover', 
                  bgcolor: 'black',
                  opacity: 0.5
                }}
              />
            )}
          </Box>
        );
      default:
        return (
          <Box 
            sx={{ 
              height: 160, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            {getAssetIcon()}
          </Box>
        );
    }
  };
  
  // Get file name or title
  const getAssetName = () => {
    return asset.originalname || asset.key?.split('/').pop() || 'Unnamed asset';
  };

  // Handle view action
  const handleView = () => {
    const effectiveUrl = getEffectiveUrl();
    if (effectiveUrl && onView) {
      onView(effectiveUrl);
    } else if (effectiveUrl) {
      window.open(effectiveUrl, '_blank');
    }
  };

  // Handle download action
  const handleDownload = async () => {
    try {
      // Always download the original file
      const fileKey = asset.key || asset.url;
      if (fileKey && !isLocal) {
        const originalUrl = await getSignedFileUrl(fileKey);
        const a = document.createElement('a');
        a.href = originalUrl;
        a.download = getAssetName();
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else if (isLocal) {
        const effectiveUrl = getEffectiveUrl();
        const a = document.createElement('a');
        a.href = effectiveUrl;
        a.download = getAssetName();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  return (
    <Card sx={{ position: 'relative', ...sx }} {...props}>
      {/* Preview */}
      {renderPreview()}
      
      {/* Actions */}
      {showActions && !isLoading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            display: 'flex',
            gap: 0.5,
            bgcolor: alpha('#fff', 0.7),
            borderRadius: 1,
            padding: 0.5,
          }}
        >
          {getEffectiveUrl() && (
            <Tooltip title="View">
              <IconButton size="small" onClick={handleView}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {getEffectiveUrl() && (
            <Tooltip title="Download">
              <IconButton 
                size="small" 
                onClick={handleDownload}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => onDelete(asset)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      
      {/* Asset details */}
      <CardContent sx={{ py: 1 }}>
        <Typography variant="body2" noWrap title={getAssetName()}>
          {getAssetName()}
        </Typography>
        
        {asset.fileType && (
          <Typography variant="caption" color="textSecondary">
            {asset.fileType.toUpperCase()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Helper component for displaying secure assets with version support
 */
export const SecureAsset = ({ 
  assetKey, 
  type = 'image',
  alt = '', 
  onLoad,
  sx = {},
  version = 'preview',
  ...props 
}) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  
  // Handle version selection
  let key = null;
  if (assetKey && typeof assetKey === 'object') {
    const selected = selectBestVersion(assetKey, version);
    key = selected ? selected.key : assetKey.key;
  } else {
    key = assetKey;
  }
  
  const isLocal = key?.includes('blob');

  useEffect(() => {
    if (key && !isLocal) {
      setIsLoading(true);
      getSignedFileUrl(key)
        .then(url => {
          setSignedUrl(url);
          setIsLoading(false);
          if (onLoad) onLoad(url);
        })
        .catch(error => {
          console.error('Error fetching signed URL:', error);
          setIsLoading(false);
        });
    } else if (isLocal && onLoad) {
      onLoad(key);
    }
  }, [key, isLocal, onLoad]);

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          ...sx
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (type === 'image' && (signedUrl || isLocal)) {
    return (
      <Box 
        component="img" 
        src={isLocal ? key : signedUrl}
        alt={alt}
        sx={{ 
          maxWidth: '100%',
          objectFit: 'contain',
          ...sx 
        }}
        {...props}
      />
    );
  }

  if (type === 'video' && (signedUrl || isLocal)) {
    return (
      <Box 
        component="video" 
        src={isLocal ? key : signedUrl}
        controls
        sx={{ 
          maxWidth: '100%',
          ...sx 
        }}
        {...props}
      />
    );
  }

  // Fallback for other types or when URL is not available
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        ...sx
      }}
    >
      {type === 'image' && <ImageIcon fontSize="large" />}
      {type === 'video' && <VideoIcon fontSize="large" />}
      {type === 'audio' && <AudioIcon fontSize="large" />}
      {type === 'pdf' && <PdfIcon fontSize="large" />}
      {!['image', 'video', 'audio', 'pdf'].includes(type) && <DocumentIcon fontSize="large" />}
    </Box>
  );
};

export default AssetPreview;